/**
 * API 가드 정책 정의
 *
 * 이 파일에서는 각 API가 어떤 조건을 필요로 하는지만 정의합니다.
 * - 기본값: 'account' (로그인 + 계좌 선택 필요)
 * - 'login': 로그인이 필요
 * - 'public': 인증 없이 접근 가능
 *
 * 실제 처리 로직(로그인 모달 열기, 계좌 선택 안내 등)은
 * 각 앱(desktop, mobile 등)에서 GuardConfig로 정의합니다.
 *
 * 정책 적용 우선순위:
 * 1. methodPolicies (메서드별 정책)
 * 2. serviceGuardTypes (서비스 전체 정책)
 * 3. 기본값 'account'
 */
/**
 * Auth 서비스 가드 정책
 * getAccountJwtToken: 계좌 토큰 발급용이므로 'login'만 필요
 * 나머지 public
 */
const authPolicy = {
    loginForWeb: 'public',
    loginForApp: 'public',
    refreshTokenForWeb: 'public',
    refreshTokenForApp: 'public',
    sendSmsVerificationCode: 'public',
    verifySmsVerificationCode: 'public',
    sendEmailVerificationCode: 'public',
    verifyEmailVerificationCode: 'public',
    getAccountJwtToken: 'login',
    logout: 'login',
    logoutAll: 'login',
};
/**
 * Stock 서비스 가드 정책
 */
const stockPolicy = {
    getOrderInfo: 'public', // 비로그인에서도 주문 화면(증거금율 등) 표시를 위해 호출 필요
    getOrderStocks: 'public', // /stocks/orders 경로
    getPossessionStocks: 'account', // accountNo 파라미터 필요
    getPositionStocks: 'account', // accountNo 파라미터 필요
    getAccountStocks: 'account', // accountNo 파라미터 필요
};
/**
 * Account 서비스 가드 정책
 * 일부 메서드는 accountNo 필요, 일부는 login만 필요
 */
const accountPolicy = {
    getAccountInfo: 'login', // accountNo 없음
    updateAccountInfo: 'login', // accountNo 없음
    updatePassword: 'account', // accountNo 파라미터 필요
    requestWithdrawal: 'account', // accountNo 파라미터 필요
    requestDeposit: 'account', // accountNo 파라미터 필요
    createAccount: 'login', // accountNo 없음
    getDepositsWithdrawalsInfo: 'account', // accountNo 파라미터 필요
    getAvailableAmount: 'login', // accountNo 없음
};
const memberPolicy = {
    approve: 'public',
    changePassword: 'public',
    joinIndividual: 'public',
    joinCorporate: 'public',
    findId: 'public',
    getAccessHistory: 'login',
    getInfo: 'login',
    updateInfo: 'login',
    updatePhone: 'login',
    updateAddress: 'login',
    updateMarketingAgreement: 'login',
    /** 준회원 서류 제출: 계좌 없이 로그인만 필요 */
    submitIndividualDocuments: 'login',
    submitCorporateDocuments: 'login',
};
/**
 * 서비스별 메서드 정책 맵
 * 서비스 클래스 이름을 키로 사용하여 정책을 찾을 수 있습니다.
 */
export const methodPolicies = {
    AuthService: authPolicy,
    StockService: stockPolicy,
    AccountService: accountPolicy,
    MemberService: memberPolicy,
};
/**
 * 서비스 전체 가드 타입 맵
 * 서비스의 모든 메서드에 동일한 가드가 적용되는 경우
 */
export const serviceGuardTypes = {
    // 로그인 필요: 모든 메서드가 로그인 상태 필요
    WatchListService: 'login',
    NotificationService: 'login', // 알림 조회/관리 서비스
    CommunityQNAService: 'login', // QnA 서비스만 로그인 필요
    // 계좌 필요: 모든 메서드가 로그인 + 계좌 선택 필요
    OrderService: 'account',
    TradeService: 'account',
    ExecutionService: 'account',
    PaymentService: 'account',
    AssetService: 'account',
    // 퍼블릭: 모든 메서드가 인증 없이 접근 가능
    TermsService: 'public',
    QuoteService: 'public',
    KcbPhoneVerificationService: 'public',
    CommunityHealthCheckService: 'public',
    CommunityPressControllerService: 'public',
    CommunityNoticeService: 'public',
    CommunityFAQService: 'public',
    CommunityEventService: 'public',
};
//# sourceMappingURL=policies.js.map